import React from 'react';
import clsx from 'clsx';
import { Inbox } from 'lucide-react';

export interface Column<T> {
  key: string;
  header: string;
  width?: string;
  render: (row: T) => React.ReactNode;
}

interface Props<T> {
  rows: T[];
  columns: Column<T>[];
  empty?: string;
  zebra?: boolean;
  stickyHeader?: boolean;
}

export function DataTable<T>({ rows, columns, empty = 'No records.', zebra = false, stickyHeader = false }: Props<T>) {
  const gridCols = columns.map((c) => c.width ?? '1fr').join(' ');

  if (!rows.length) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-surface px-6 py-12 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-soft text-primary">
          <Inbox size={22} />
        </span>
        <p className="max-w-md text-sm text-text-muted">{empty}</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-soft">
      {/* Header */}
      <div
        className={clsx(
          'grid border-b border-border bg-surface-alt px-5 py-2.5',
          stickyHeader && 'sticky top-0 z-10',
        )}
        style={{ gridTemplateColumns: gridCols, gap: '12px' }}
      >
        {columns.map((c) => (
          <span key={c.key} className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">
            {c.header}
          </span>
        ))}
      </div>

      {/* Rows */}
      <div className="divide-y divide-border">
        {rows.map((row, i) => (
          <div
            key={i}
            className={clsx(
              'grid items-center px-5 py-3.5 text-sm transition-colors',
              zebra && i % 2 === 1 ? 'bg-surface-alt/40' : '',
              'hover:bg-surface-alt/60',
            )}
            style={{ gridTemplateColumns: gridCols, gap: '12px' }}
          >
            {columns.map((c) => (
              <div key={c.key}>{c.render(row)}</div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
